// ── Capability Registry ──
// Single source of truth for all module/feature capabilities.
// Additive mapping of existing Koredor modules — does NOT modify them.

import type { CapabilityDefinition } from "./types";

const ALL_ROLES = [
  "super_admin", "owner", "cfo", "finance_manager", "bookkeeper",
  "ops_manager", "admin", "warehouse_manager", "head_of_installation",
  "procurement_manager", "inventory_manager", "installer", "subcontractor", "sales",
] as const;

const ADMIN_ROLES = ["super_admin", "owner", "ops_manager", "admin"] as const;
const FINANCE_ROLES = ["super_admin", "owner", "cfo", "finance_manager", "bookkeeper"] as const;
const OPS_ROLES = ["super_admin", "owner", "ops_manager", "admin", "head_of_installation"] as const;

function cap(partial: Partial<CapabilityDefinition> & Pick<CapabilityDefinition, "id" | "label" | "category">): CapabilityDefinition {
  return {
    enabled: true,
    tier: "core",
    dependencies: [],
    conflicts: [],
    rolesAllowed: [...ALL_ROLES],
    uiSurfaces: [],
    behaviorMode: "active",
    safeFallback: "hide",
    auditRequired: false,
    description: "",
    influence: { ui: true, intelligence: false, recommendations: false, financials: false },
    fallbackBehavior: { onDisable: "remove_section", preserveData: true, preserveAudit: true },
    ...partial,
  };
}

// ── Registry ──

export const CAPABILITY_REGISTRY: CapabilityDefinition[] = [
  // ═══ CRM / Sales ═══
  cap({
    id: "crm.pipeline", label: "Sales Pipeline", category: "crm",
    description: "Lead-to-close pipeline with stage management",
    uiSurfaces: ["/pipeline", "/pipeline/:id"],
    rolesAllowed: [...ADMIN_ROLES, "sales"],
    influence: { ui: true, intelligence: true, recommendations: true, financials: false },
  }),
  cap({
    id: "crm.intake_review", label: "Intake Review", category: "crm",
    description: "Lead qualification and intake workspace",
    uiSurfaces: ["/intake-review"],
    rolesAllowed: [...ADMIN_ROLES, "sales"],
    dependencies: ["crm.pipeline"],
  }),
  cap({
    id: "crm.lead_integrity", label: "Lead Integrity Engine", category: "intelligence",
    description: "Automated lead scoring and data quality checks",
    tier: "advanced",
    uiSurfaces: ["/lead-integrity"],
    rolesAllowed: [...ADMIN_ROLES],
    dependencies: ["crm.pipeline"],
    influence: { ui: true, intelligence: true, recommendations: true, financials: false },
  }),
  cap({
    id: "crm.follow_up", label: "Follow-Up Engine", category: "crm",
    description: "Automated follow-up task generation and tracking",
    tier: "advanced",
    uiSurfaces: ["/follow-up-engine"],
    rolesAllowed: [...ADMIN_ROLES, "sales"],
    dependencies: ["crm.pipeline"],
    influence: { ui: true, intelligence: true, recommendations: true, financials: false },
  }),
  cap({
    id: "crm.messages", label: "Messaging", category: "communications",
    description: "Internal and customer messaging center",
    uiSurfaces: ["/messages"],
  }),

  // ═══ Opportunities / Quoting ═══
  cap({
    id: "sales.opportunities", label: "Opportunities & Quoting", category: "sales",
    description: "Quote creation, comparison, and approval workflows",
    uiSurfaces: ["/opportunities", "/opportunities/:id"],
    rolesAllowed: [...ADMIN_ROLES, "sales"],
    influence: { ui: true, intelligence: false, recommendations: false, financials: true },
  }),
  cap({
    id: "sales.offerings", label: "Offerings Catalog", category: "sales",
    description: "Product and service catalog for quoting",
    uiSurfaces: ["/offerings"],
    rolesAllowed: [...ADMIN_ROLES, "sales"],
    dependencies: ["sales.opportunities"],
  }),

  // ═══ Operations ═══
  cap({
    id: "ops.jobs", label: "Job Management", category: "operations",
    description: "Core job lifecycle and tracking",
    uiSurfaces: ["/jobs", "/jobs/:id"],
    tier: "core",
  }),
  cap({
    id: "ops.scheduling", label: "Scheduling & Dispatch", category: "scheduling",
    description: "Calendar-based scheduling with intelligence overlay",
    uiSurfaces: ["/scheduling", "/scheduling-dispatch"],
    rolesAllowed: [...OPS_ROLES, "installer", "subcontractor"],
    dependencies: ["ops.jobs"],
    influence: { ui: true, intelligence: true, recommendations: true, financials: true },
  }),
  cap({
    id: "ops.install_readiness", label: "Install Readiness", category: "operations",
    description: "Pre-install checklist and readiness gating",
    uiSurfaces: ["/install-readiness"],
    rolesAllowed: [...OPS_ROLES],
    dependencies: ["ops.jobs", "ops.scheduling"],
  }),
  cap({
    id: "ops.permitting", label: "Permitting Portal", category: "compliance",
    description: "Permit tracking and management",
    uiSurfaces: ["/permitting"],
    rolesAllowed: [...ADMIN_ROLES],
    dependencies: ["ops.jobs"],
  }),
  cap({
    id: "ops.closeout", label: "Project Closeout", category: "operations",
    description: "Final inspection, sign-off, and closeout workflow",
    uiSurfaces: ["/closeout"],
    rolesAllowed: [...OPS_ROLES],
    dependencies: ["ops.jobs"],
  }),
  cap({
    id: "ops.qa", label: "QA Inspection", category: "qa",
    description: "Quality assurance forms and photo capture",
    uiSurfaces: ["/qa-form"],
    rolesAllowed: [...OPS_ROLES, "installer"],
    dependencies: ["ops.jobs"],
  }),
  cap({
    id: "ops.issues", label: "Issue Tracking", category: "operations",
    description: "Issue reporting, escalation, and resolution",
    uiSurfaces: ["/issues", "/issue-form"],
  }),
  cap({
    id: "ops.service_tickets", label: "Service Tickets", category: "operations",
    description: "Post-install service and warranty tracking",
    uiSurfaces: ["/service-tickets"],
    rolesAllowed: [...OPS_ROLES],
    dependencies: ["ops.jobs"],
  }),

  // ═══ Materials & Warehouse ═══
  cap({
    id: "materials.ordering", label: "Ordering Queue", category: "materials",
    description: "Material ordering workflow and PO management",
    uiSurfaces: ["/ordering-queue"],
    rolesAllowed: [...ADMIN_ROLES, "warehouse_manager", "procurement_manager"],
    dependencies: ["ops.jobs"],
    influence: { ui: true, intelligence: false, recommendations: false, financials: true },
  }),
  cap({
    id: "materials.warehouse", label: "Warehouse Management", category: "warehouse",
    description: "Warehouse readiness, staging, and execution",
    uiSurfaces: ["/warehouse"],
    rolesAllowed: [...ADMIN_ROLES, "warehouse_manager", "inventory_manager"],
    dependencies: ["ops.jobs"],
  }),
  cap({
    id: "materials.inventory", label: "Inventory Module", category: "warehouse",
    description: "SKU tracking, stock levels, and reorder points",
    uiSurfaces: ["/inventory"],
    rolesAllowed: [...ADMIN_ROLES, "warehouse_manager", "inventory_manager", "procurement_manager"],
  }),
  cap({
    id: "materials.vendors", label: "Vendor Management", category: "materials",
    description: "Vendor catalog, pricing, and PO tracking",
    uiSurfaces: ["/vendors"],
    rolesAllowed: [...ADMIN_ROLES, "procurement_manager"],
  }),
  cap({
    id: "materials.product_registry", label: "Product Registry", category: "materials",
    description: "Master product database and SKU mapping",
    tier: "advanced",
    uiSurfaces: ["/product-registry"],
    rolesAllowed: [...ADMIN_ROLES],
  }),

  // ═══ Contractors ═══
  cap({
    id: "contractors.management", label: "Contractor Management", category: "operations",
    description: "Contractor profiles, scorecards, and reliability tracking",
    uiSurfaces: ["/contractors", "/contractors/:id"],
    rolesAllowed: [...OPS_ROLES],
  }),
  cap({
    id: "contractors.onboarding", label: "Contractor Onboarding", category: "operations",
    description: "New contractor intake and onboarding workflow",
    uiSurfaces: ["/contractor-onboarding", "/contractor-intake"],
    rolesAllowed: [...ADMIN_ROLES],
    dependencies: ["contractors.management"],
  }),

  // ═══ Customers ═══
  cap({
    id: "customers.management", label: "Customer Management", category: "crm",
    description: "Customer profiles and property verification",
    uiSurfaces: ["/customers", "/customers/:name"],
  }),

  // ═══ Finance ═══
  cap({
    id: "finance.accounting", label: "Accounting", category: "finance",
    description: "Financial views, AP/AR, and cost tracking",
    uiSurfaces: ["/accounting"],
    rolesAllowed: [...FINANCE_ROLES],
    auditRequired: true,
    influence: { ui: true, intelligence: false, recommendations: false, financials: true },
  }),
  cap({
    id: "finance.fie", label: "Financial Intelligence Engine", category: "finance",
    description: "Earnings forecasting, attribution ledger, and partner distributions",
    tier: "enterprise",
    uiSurfaces: ["/earnings-intelligence"],
    rolesAllowed: [...FINANCE_ROLES],
    auditRequired: true,
    influence: { ui: true, intelligence: true, recommendations: true, financials: true },
    fallbackBehavior: { onDisable: "degrade_to_readonly", preserveData: true, preserveAudit: true },
  }),

  // ═══ Intelligence ═══
  cap({
    id: "intelligence.dashboard", label: "Intelligence Dashboard", category: "intelligence",
    description: "Cross-module operational intelligence and insights",
    tier: "advanced",
    uiSurfaces: ["/intelligence"],
    rolesAllowed: [...ADMIN_ROLES],
    influence: { ui: true, intelligence: true, recommendations: true, financials: false },
  }),
  cap({
    id: "intelligence.automations", label: "Automation Engine", category: "intelligence",
    description: "Workflow automations and triggered actions",
    tier: "advanced",
    uiSurfaces: ["/automations"],
    rolesAllowed: [...ADMIN_ROLES],
  }),

  // ═══ Reporting ═══
  cap({
    id: "reporting.core", label: "Reports", category: "reporting",
    description: "Standard operational and financial reports",
    uiSurfaces: ["/reports"],
    rolesAllowed: [...ADMIN_ROLES, ...FINANCE_ROLES],
  }),
  cap({
    id: "reporting.marketing", label: "Marketing Performance", category: "reporting",
    description: "Marketing channel analytics and ROI tracking",
    uiSurfaces: ["/marketing-attribution"],
    rolesAllowed: [...ADMIN_ROLES],
    dependencies: ["crm.pipeline"],
  }),
  cap({
    id: "reporting.disposition", label: "Disposition Report", category: "reporting",
    description: "Lead disposition and conversion analysis",
    uiSurfaces: ["/disposition-report"],
    rolesAllowed: [...ADMIN_ROLES, "sales"],
  }),

  // ═══ Admin ═══
  cap({
    id: "admin.settings", label: "Settings", category: "admin",
    description: "System configuration, roles, and preferences",
    uiSurfaces: ["/settings"],
    rolesAllowed: ["super_admin", "owner", "ops_manager"],
    auditRequired: true,
  }),
  cap({
    id: "admin.system_audit", label: "System Audit", category: "admin",
    description: "Workflow audit trail and system health monitoring",
    tier: "internal",
    uiSurfaces: ["/system-audit"],
    rolesAllowed: ["super_admin", "owner"],
    auditRequired: true,
    behaviorMode: "internal_only",
  }),
  cap({
    id: "admin.alerts", label: "Alert Management", category: "admin",
    description: "Alert history and notification management",
    uiSurfaces: ["/alerts"],
    rolesAllowed: [...ADMIN_ROLES],
  }),
  cap({
    id: "admin.friction_log", label: "Friction Log", category: "admin",
    description: "Internal UX friction tracking and resolution",
    tier: "internal",
    uiSurfaces: ["/friction-log"],
    rolesAllowed: ["super_admin", "owner"],
    behaviorMode: "internal_only",
  }),
  cap({
    id: "admin.control_plane", label: "Control Plane", category: "admin",
    description: "Capability governance and configuration management",
    tier: "internal",
    uiSurfaces: ["/control-plane"],
    rolesAllowed: ["super_admin", "owner"],
    auditRequired: true,
    behaviorMode: "internal_only",
  }),

  // ═══ Portals ═══
  cap({
    id: "portals.customer", label: "Customer Portal", category: "portal",
    description: "External customer-facing project tracking portal",
    uiSurfaces: ["/portal", "/portal/:jobId"],
  }),
  cap({
    id: "portals.contractor", label: "Contractor Portal", category: "portal",
    description: "External contractor-facing work order portal",
    uiSurfaces: ["/contractor-portal"],
  }),
  cap({
    id: "portals.hub", label: "Portal Hub", category: "portal",
    description: "Internal portal management and invitation center",
    uiSurfaces: ["/portals"],
    rolesAllowed: [...ADMIN_ROLES],
  }),

  // ═══ Scheduling Intelligence ═══
  cap({
    id: "scheduling.intelligence", label: "Scheduling Intelligence", category: "scheduling",
    description: "Revenue-aware scheduling with readiness engine and recommendations",
    tier: "enterprise",
    uiSurfaces: ["/scheduling-dispatch"],
    rolesAllowed: [...OPS_ROLES],
    dependencies: ["ops.scheduling", "ops.jobs"],
    influence: { ui: true, intelligence: true, recommendations: true, financials: true },
    fallbackBehavior: { onDisable: "degrade_to_readonly", preserveData: true, preserveAudit: true },
  }),

  // ═══ Lead Sources ═══
  cap({
    id: "crm.lead_sources", label: "Lead Source Management", category: "crm",
    description: "Lead source tracking, cost analysis, and channel management",
    uiSurfaces: ["/lead-sources"],
    rolesAllowed: [...ADMIN_ROLES],
    dependencies: ["crm.pipeline"],
  }),
];

// ── Lookup helpers ──

const registryMap = new Map(CAPABILITY_REGISTRY.map(c => [c.id, c]));

export function getCapabilityById(id: string): CapabilityDefinition | undefined {
  return registryMap.get(id);
}

export function getCapabilitiesByCategory(category: string): CapabilityDefinition[] {
  return CAPABILITY_REGISTRY.filter(c => c.category === category);
}

export function getCapabilitiesByTier(tier: string): CapabilityDefinition[] {
  return CAPABILITY_REGISTRY.filter(c => c.tier === tier);
}

export function getAllCategories(): string[] {
  return [...new Set(CAPABILITY_REGISTRY.map(c => c.category))];
}
